<template>
  <input
    ref="root"
    :max="max"
    :min="min"
    :type="inputType"
    :value="modelValue"
    class="h-10 w-[100px] rounded border bg-secondary-450 text-center text-sm font-semibold text-primary"
    data-testid="price-input"
    @blur="blur"
    @focus="focus"
    @input="inputChange"
    @keydown="handleKeydown"
  />
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

const { min, max, modelValue } = defineProps<{
  modelValue: number
  max: number
  min: number
}>()

const emit = defineEmits<{
  'update:model-value': [reduction: number]
  focus: [FocusEvent]
  blur: [FocusEvent]
}>()

const isActive = ref(false)
const root = ref<HTMLInputElement>()
const debounceTimer = ref<number>()
const isArrowKey = ref(false)

const focus = (e: FocusEvent) => {
  isActive.value = true
  emit('focus', e)
}

const inputType = computed(() => (isActive.value ? 'number' : 'text'))

const blur = (e: FocusEvent) => {
  isActive.value = false

  // Clear any pending debounce and emit final value on blur
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value)
    debounceTimer.value = undefined
  }

  emit('blur', e)
}

const handleKeydown = (e: KeyboardEvent) => {
  // Detect if arrow up/down keys are pressed
  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
    isArrowKey.value = true
  } else {
    isArrowKey.value = false
  }
}

const inputChange = (event: Event) => {
  const { value } = event.target as HTMLInputElement
  if (value === modelValue.toString()) {
    return
  }

  const adjustedValue = parseInt(value, 10)

  if (!isNaN(adjustedValue) && modelValue !== adjustedValue) {
    if (debounceTimer.value) {
      clearTimeout(debounceTimer.value)
    }

    // If arrow keys were used, emit immediately
    if (isArrowKey.value) {
      emit('update:model-value', adjustedValue)
      isArrowKey.value = false
    } else {
      // Otherwise, debounce the update (800ms delay)
      debounceTimer.value = setTimeout(() => {
        emit('update:model-value', adjustedValue)
      }, 800) as unknown as number
    }
  }
}
</script>
